import React, { useMemo } from 'react';
import { mdiCodeJson, mdiLightningBolt } from '@mdi/js';
import Icon from '@mdi/react';
import { CodingStatusType } from '../../../../api';
import { renderDate, Variables } from '../../../..';
import { TimelineStatusTag } from '../timeline-status-tag';
import * as Cd from './coding-detail.styles';
import { Coding } from './coding-detail.types';
import {
    codingHeadlineTitle,
    codingStatusToTone,
    computeCodingFieldChanges,
    formatCodingChangesSummary,
    getCodingPayload,
    parseCodingNotes,
    shouldCompareCodingPayloads,
} from './coding-detail.utils';
import { PayloadChangesCard } from './payload-changes-card';

interface ICodingEmbeddedPanelProps {
    coding: Coding;
    embedded?: boolean;
}

export const CodingEmbeddedPanel: React.FunctionComponent<ICodingEmbeddedPanelProps> = ({
    coding,
    embedded = true,
}) => {
    const isSubmitted = coding.status === CodingStatusType.SUBMITTED;
    const isCancelled = coding.status === CodingStatusType.CANCELLED;
    const shouldAnalyzeDiff = shouldCompareCodingPayloads(coding.status);

    const rules = useMemo(() => parseCodingNotes(coding.notes), [coding.notes]);
    const beforePayload = useMemo(() => getCodingPayload(coding, 'before'), [coding]);
    const afterPayload = useMemo(() => getCodingPayload(coding, 'after'), [coding]);
    const fieldChanges = useMemo(
        () => (shouldAnalyzeDiff ? computeCodingFieldChanges(beforePayload, afterPayload) : []),
        [shouldAnalyzeDiff, beforePayload, afterPayload],
    );
    const changesSummary = useMemo(
        () =>
            isSubmitted
                ? 'awaiting coding results'
                : isCancelled
                  ? 'not compared'
                  : formatCodingChangesSummary(fieldChanges),
        [isSubmitted, isCancelled, fieldChanges],
    );
    const notComparedMessage = isSubmitted
        ? 'Coding is still submitted. Diff analysis runs after coding completes.'
        : isCancelled
          ? 'Coding was cancelled, so its before and after payloads are not compared.'
          : undefined;

    const statusLabel = coding.status || 'Unknown';
    const headlineTitle = codingHeadlineTitle(coding.status);

    return (
        <Cd.CdPanel $embedded={embedded}>
            <Cd.CdHeadlineCard $embedded={embedded}>
                <Cd.CdIconWrap>
                    <Icon path={mdiCodeJson} size={0.9} color={Variables.ThemeLight.PrimaryConstant6} />
                </Cd.CdIconWrap>
                <Cd.CdHeadlineMain>
                    <Cd.CdHeadlineTitleRow>
                        <Cd.CdHeadlineTitle>{headlineTitle}</Cd.CdHeadlineTitle>
                        <TimelineStatusTag tone={codingStatusToTone(coding.status)} label={statusLabel} />
                    </Cd.CdHeadlineTitleRow>
                    <Cd.CdHeadlineMeta>
                        Automated pass by <strong>{coding.vendor || 'Unknown vendor'}</strong>
                        {coding.created ? <> · {renderDate(coding.created)}</> : null}
                    </Cd.CdHeadlineMeta>
                    <Cd.CdHeadlineSummary>
                        <Icon path={mdiLightningBolt} size={0.67} color={Variables.ThemeLight.AttentionConstant6} />
                        <span>
                            <strong>{rules.length} rules</strong> applied · <strong>{changesSummary}</strong>
                        </span>
                    </Cd.CdHeadlineSummary>
                </Cd.CdHeadlineMain>
                <Cd.CdHeadlineAside>
                    <Cd.CdLabel>Coding ID</Cd.CdLabel>
                    <Cd.CdMonoValue>{coding.id}</Cd.CdMonoValue>
                </Cd.CdHeadlineAside>
            </Cd.CdHeadlineCard>

            <Cd.CdSectionCard $embedded={embedded}>
                <Cd.CdSectionTitle>Rules Applied</Cd.CdSectionTitle>
                {rules.length > 0 ? (
                    <Cd.CdRulesList>
                        {rules.map(rule => (
                            <Cd.CdRuleCard key={`${rule.n}-${rule.text}`}>
                                <Cd.CdRuleBadge>Rule {rule.n}</Cd.CdRuleBadge>
                                <Cd.CdRuleText>{rule.text}</Cd.CdRuleText>
                            </Cd.CdRuleCard>
                        ))}
                    </Cd.CdRulesList>
                ) : (
                    <Cd.CdEmptyState>No rules recorded for this coding pass.</Cd.CdEmptyState>
                )}
            </Cd.CdSectionCard>

            <PayloadChangesCard
                embedded={embedded}
                beforePayload={beforePayload}
                afterPayload={afterPayload}
                fieldChanges={fieldChanges}
                notComparedMessage={notComparedMessage}
                noAfterPayloadMessage="No after payload was recorded for this coding pass."
            />
        </Cd.CdPanel>
    );
};
